import React from 'react';
import { Activity, Users, RefreshCw, Calendar, TrendingUp, Heart } from 'lucide-react';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import {
  useHealthMetricsSummary,
  useHealthMetricsDaily,
  useHealthMetricsByTenant,
  useHealthMetricsToday,
} from '../hooks/useReports';
import { formatNumber, formatCompactNumber, formatChartDate } from '../utils/formatters';
import DataTable from '../components/DataTable';

function KPI({ label, value, icon: Icon, loading, color = 'amber' }) {
  const colors = {
    amber: 'bg-amber-100 text-amber-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm font-medium text-gray-500">{label}</p>
          <p className={`mt-1 text-xl sm:text-2xl font-bold text-gray-900 ${loading ? 'animate-pulse' : ''}`}>
            {loading ? '–' : formatCompactNumber(value ?? 0)}
          </p>
        </div>
        {Icon && (
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colors[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, color }) {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    amber: 'bg-amber-100 text-amber-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
  };
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className={`p-2 rounded-lg ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    </div>
  );
}

export default function HealthMetrics() {
  const { data: summaryData, isLoading: summaryLoading } = useHealthMetricsSummary();
  const { data: dailyData, isLoading: dailyLoading } = useHealthMetricsDaily(30);
  const { data: tenantData, isLoading: tenantLoading } = useHealthMetricsByTenant(30);
  const { data: todayData, isLoading: todayLoading } = useHealthMetricsToday();

  const summary = summaryData?.data?.[0] || {};
  const today = todayData?.data?.[0] || {};
  const daily = dailyData?.data || [];
  const tenants = tenantData?.data || [];

  // Chart data (reverse so oldest is first) - dates in EST
  const chartData = [...daily].reverse().map((d) => ({
    date: formatChartDate(d.metric_date),
    users: parseInt(d.active_users) || 0,
    syncs: parseInt(d.total_syncs) || 0,
  }));

  // Tenant table columns
  const tenantColumns = [
    { key: 'tenant_name', label: 'Tenant', sortable: true },
    { key: 'active_users', label: 'Active Users', sortable: true, format: 'number' },
    { key: 'total_syncs', label: 'Total Syncs', sortable: true, format: 'number' },
  ];

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Health Metrics</h2>
        <p className="text-gray-500 mt-1 text-sm">
          Apple HealthKit & Google Fit data from members
        </p>
      </div>

      {/* Summary KPIs */}
      <section>
        <SectionHeader icon={Activity} title="Overall Summary" color="amber" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI
            label="Total Users Tracking"
            value={summary.total_users}
            icon={Users}
            loading={summaryLoading}
            color="blue"
          />
          <KPI
            label="Total Syncs"
            value={summary.total_records}
            icon={RefreshCw}
            loading={summaryLoading}
            color="amber"
          />
          <KPI
            label="Users Today"
            value={today.users_today}
            icon={Heart}
            loading={todayLoading}
            color="green"
          />
          <KPI
            label="Syncs Today"
            value={today.syncs_today}
            icon={TrendingUp}
            loading={todayLoading}
            color="purple"
          />
        </div>
      </section>

      {/* Daily Trends */}
      <section>
        <SectionHeader icon={Calendar} title="30-Day Trends" color="blue" />
        {chartData.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Active Users Chart */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-sm font-medium text-gray-500 mb-3">Daily Active Users</p>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="userGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={40} />
                  <Tooltip
                    formatter={(value) => [formatNumber(value), 'Users']}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Area type="monotone" dataKey="users" stroke="#3b82f6" fill="url(#userGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Syncs Chart */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-sm font-medium text-gray-500 mb-3">Daily Syncs</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData}>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={50} />
                  <Tooltip
                    formatter={(value) => [formatNumber(value), 'Syncs']}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Bar dataKey="syncs" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </section>

      {/* Tenants Table */}
      <section>
        <SectionHeader icon={Users} title="Tenants with Health Data (Last 30 Days)" color="green" />
        <DataTable
          data={tenants}
          columns={tenantColumns}
          loading={tenantLoading}
          emptyMessage="No tenant data available"
        />
      </section>
    </div>
  );
}
